import { ChevronDown, ExternalLink } from 'lucide-react'
import { useState } from 'react'

import { HubspotOrganizationData } from '@/api/organization-hubspot-client'
import { Button, buttonVariants } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import { useQueryGetOrganizationHubspotData } from '@/services/hubspot.service'

// Get HubSpot portal ID from environment config
const getHubspotPortalId = () =>
  globalThis.EnvConfig?.env?.HUBSPOT_PORTAL_ID || '25127966'

export const getHubspotCompanyUrl = (companyId: string) =>
  `https://app.hubspot.com/contacts/${getHubspotPortalId()}/company/${companyId}`

export type FieldConfig = {
  key: string
  label: string
  propertyKey?: string
}

const BASIC_FIELDS: FieldConfig[] = [
  { key: 'type', label: 'Type', propertyKey: 'type' },
  { key: 'sparte', label: 'Sparte', propertyKey: 'sparte' },
  { key: 'description', label: 'Description', propertyKey: 'description' },
  { key: 'marktrolle', label: 'Marktrolle', propertyKey: 'marktrolle' },
  {
    key: 'ziel_account_executive',
    label: 'Account Executive',
    propertyKey: 'ziel_account_executive'
  },
  { key: 'closedate', label: 'Close Date', propertyKey: 'closedate' },
  { key: 'pricing_tier', label: 'Pricing Tier' },
  {
    key: 'revenue_stream',
    label: 'Revenue Stream',
    propertyKey: 'revenue_stream'
  },
  { key: 'website', label: 'Website', propertyKey: 'website' }
]

const SIZE_MRR_FIELDS: FieldConfig[] = [
  { key: 'company_size', label: 'Company Size' },
  { key: 'current_mrr', label: 'Current MRR' },
  { key: 'potential_mrr', label: 'Potential MRR' },
  {
    key: 'potential_mrr__next_year_',
    label: 'Potential MRR (Next Year)',
    propertyKey: 'potential_mrr__next_year_'
  },
  {
    key: 'total_revenue',
    label: 'Total Revenue',
    propertyKey: 'total_revenue'
  },
  {
    key: 'annualrevenue',
    label: 'Annual Revenue',
    propertyKey: 'annualrevenue'
  },
  {
    key: 'anzahl_kunden__c',
    label: 'Anzahl Kunden',
    propertyKey: 'anzahl_kunden__c'
  },
  {
    key: 'customer_data_points__cdp_',
    label: 'Customer Data Points (CDP)',
    propertyKey: 'customer_data_points__cdp_'
  },
  { key: 'numberofemployees', label: 'Number of Employees' },
  {
    key: 'no__of_seats',
    label: 'Number of Seats',
    propertyKey: 'no__of_seats'
  },
  { key: 'no_of_users', label: 'Number of Users' }
]

const ADOPTION_FIELDS: FieldConfig[] = [
  { key: 'usecases_implemented', label: 'Use Cases Implemented' },
  { key: 'active_features', label: 'Active Features' },
  {
    key: 'aktive_produkte__c',
    label: 'Aktive Produkte',
    propertyKey: 'aktive_produkte__c'
  },
  {
    key: 'number_of_implemented_use_cases',
    label: 'Number of Implemented Use Cases',
    propertyKey: 'number_of_implemented_use_cases'
  }
]

const IT_LANDSCAPE_FIELDS: FieldConfig[] = [
  {
    key: 'customer_portal',
    label: 'Customer Portal',
    propertyKey: 'customer_portal'
  },
  {
    key: 'crm_system__c',
    label: 'CRM System',
    propertyKey: 'crm_system__c'
  },
  {
    key: 'archive_system',
    label: 'Archive System',
    propertyKey: 'archive_system'
  },
  {
    key: 'additional_software_in_use',
    label: 'Additional Software in Use',
    propertyKey: 'additional_software_in_use'
  }
]

const KNOWN_KEYS = new Set([
  ...BASIC_FIELDS.map((f) => f.propertyKey || f.key),
  ...SIZE_MRR_FIELDS.map((f) => f.propertyKey || f.key),
  ...ADOPTION_FIELDS.map((f) => f.propertyKey || f.key),
  ...IT_LANDSCAPE_FIELDS.map((f) => f.propertyKey || f.key),
  // Exclude keys that are already shown in the header or are internal HubSpot metadata
  'company_id',
  'company_name',
  'domain'
])

export const getFieldValue = (
  data: HubspotOrganizationData,
  field: FieldConfig
): string => {
  // If there's a direct key on the data object, use it
  const directKey = field.key as keyof HubspotOrganizationData
  if (
    directKey in data &&
    data[directKey] !== null &&
    data[directKey] !== undefined
  ) {
    const val = data[directKey]
    if (typeof val === 'string') return val
    if (typeof val === 'object') return JSON.stringify(val)
    return String(val)
  }

  // Otherwise, look in properties using the propertyKey
  const propKey = field.propertyKey || field.key
  const val = data.properties?.[propKey]
  if (val === null || val === undefined) return ''
  if (typeof val === 'string') return val
  return JSON.stringify(val)
}

const formatPropertyLabel = (key: string): string => {
  return key
    .replace(/__c$/g, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bHs\b/g, 'HS')
    .replace(/\bMrr\b/g, 'MRR')
    .replace(/\bCdp\b/g, 'CDP')
    .replace(/\bCrm\b/g, 'CRM')
}

const FieldValue = ({ value }: { value: string }) => (
  <dd
    className={value ? 'break-words text-sm' : 'text-sm text-muted-foreground'}
    title={value || undefined}
  >
    {value || 'Not set'}
  </dd>
)

const FieldGroup = ({
  title,
  fields,
  data
}: {
  title: string
  fields: FieldConfig[]
  data: HubspotOrganizationData
}) => (
  <section aria-label={title} className="rounded-lg border bg-card">
    <h3 className="border-b px-5 py-3 text-base font-semibold">{title}</h3>
    <dl className="px-5 pb-3 pt-1">
      {fields.map((field) => (
        <div
          className="grid grid-cols-[minmax(8rem,2fr)_3fr] gap-3 border-b border-border/60 py-2 last:border-0"
          key={field.key}
        >
          <dt className="text-sm text-muted-foreground">{field.label}</dt>
          <FieldValue value={getFieldValue(data, field)} />
        </div>
      ))}
    </dl>
  </section>
)

type OrganizationHubspotDataProps = {
  orgId?: string
  /** opens the Settings tab, where the HubSpot company is linked */
  onOpenSettings?: () => void
}

const OrganizationHubspotData = ({
  orgId,
  onOpenSettings
}: OrganizationHubspotDataProps) => {
  const { data, isLoading } = useQueryGetOrganizationHubspotData(orgId)
  const [showOther, setShowOther] = useState(false)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icons.spinner className="w-6 h-6" />
        <span className="ml-2 text-sm text-muted-foreground">
          Loading HubSpot data...
        </span>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12">
        <span className="text-sm text-muted-foreground">
          No HubSpot data available for this organization. Make sure a HubSpot
          company is linked in the Settings tab.
        </span>
        {onOpenSettings && (
          <Button onClick={onOpenSettings} size="sm" variant="outline">
            Link a company in Settings
          </Button>
        )}
      </div>
    )
  }

  // Collect remaining properties not in any field group
  const remainingProperties = Object.entries(data.properties || {})
    .filter(
      ([key, val]) =>
        !KNOWN_KEYS.has(key) && val !== null && val !== undefined && val !== ''
    )
    .sort(([a], [b]) => a.localeCompare(b))

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border bg-card px-5 py-4">
        <div className="min-w-0">
          <h2 className="text-base font-semibold">
            {data.company_name || 'Unknown Company'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {data.domain && <span>{data.domain}</span>}
            {data.domain && data.company_id && <span> &middot; </span>}
            {data.company_id && <span>HubSpot ID: {data.company_id}</span>}
            <span> &middot; read-only, synced from HubSpot</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {onOpenSettings && (
            <Button onClick={onOpenSettings} size="sm" variant="outline">
              Change linked company
            </Button>
          )}
          {data.company_id && (
            <a
              className={buttonVariants({ size: 'sm' })}
              href={getHubspotCompanyUrl(data.company_id)}
              rel="noopener noreferrer"
              target="_blank"
            >
              Open in HubSpot
              <ExternalLink className="ml-1 w-3 h-3" />
            </a>
          )}
        </div>
      </div>

      <div className="grid items-start gap-5 xl:grid-cols-2">
        <FieldGroup data={data} fields={BASIC_FIELDS} title="Basics" />
        <FieldGroup data={data} fields={SIZE_MRR_FIELDS} title="Size & MRR" />
        <FieldGroup data={data} fields={ADOPTION_FIELDS} title="Adoption" />
        <FieldGroup
          data={data}
          fields={IT_LANDSCAPE_FIELDS}
          title="IT landscape"
        />
      </div>

      {remainingProperties.length > 0 && (
        <section
          aria-label="All other properties"
          className="rounded-lg border bg-card"
        >
          <button
            aria-expanded={showOther}
            className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left"
            onClick={() => setShowOther((open) => !open)}
            type="button"
          >
            <span className="text-base font-semibold">
              All other properties{' '}
              <span className="text-sm font-normal text-muted-foreground">
                &middot; {remainingProperties.length} with a value
              </span>
            </span>
            <ChevronDown
              className={showOther ? 'h-4 w-4 rotate-180' : 'h-4 w-4'}
            />
          </button>
          {showOther && (
            <dl className="grid gap-x-6 border-t px-5 pb-3 pt-1 md:grid-cols-2">
              {remainingProperties.map(([key, val]) => (
                <div
                  className="grid grid-cols-[minmax(8rem,2fr)_3fr] gap-3 border-b border-border/60 py-2"
                  key={key}
                >
                  <dt className="text-sm text-muted-foreground">
                    {formatPropertyLabel(key)}
                  </dt>
                  <FieldValue
                    value={typeof val === 'string' ? val : JSON.stringify(val)}
                  />
                </div>
              ))}
            </dl>
          )}
        </section>
      )}
    </div>
  )
}

export default OrganizationHubspotData
